import { useState, useRef, useMemo, type Dispatch, type SetStateAction } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { WrenchScrewdriverIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { CreditCostPill } from '@/components/ui/credit-cost-pill';
import { SelectionActionBar } from '@/components/ui/selection-action-bar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { UserIcon } from '@heroicons/react/24/solid';
import { CheckIcon as CheckGlyph, XMarkIcon as XGlyph } from '@heroicons/react/20/solid';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { contactsService, type Contact } from '@/lib/services/contacts';
import {
  DataTable,
  DataTablePagination,
  TableFooter,
  useDataTableSelection,
  useDataTableColumns,
  type ColumnDefinition,
  type RowAction,
} from '@/components/data-table';
import { DataTableEditableCell } from '@/components/data-table/DataTableEditableCell';

const STORAGE_KEY = 'contacts_visibleColumns';

function LinkedInIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
    </svg>
  );
}

function toTitleCase(text: string | null | undefined): string {
  if (!text) return '';
  return text.toLowerCase().split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

interface Tool {
  id: string;
  name: string;
  description: string;
  creditCost: number;
  enabled: boolean;
}

/**
 * Verified means a check confirmed the mailbox exists: SMTP RCPT TO, ZeroBounce,
 * or a provider's own verifier. 90 is that rung. It mirrors MIN_CONFIDENCE_THRESHOLD
 * in falcon-people, so Find Email only ever produces verified addresses, and
 * MinSendConfidence in falcon-mailer, which will not send below it.
 */
const VERIFIED_CONFIDENCE = 90;

/** Text columns that can be sorted, and the contact field each one shows. */
const TEXT_SORT_FIELDS: Record<string, (c: Contact) => string | null> = {
  firstName: c => c.firstName,
  lastName: c => c.lastName,
  email: c => c.email,
  role: c => c.title,
  company: c => c.company,
  location: c => c.location,
};

/** A just-verified result, held locally so the row updates without a refetch. */
type VerifiedOverride = { enrichmentConfidence: number | null; emailStatus: string | null; emailCheckedAt?: string | null; emailCheckStatus?: string | null };

const checkedLabel = (iso: string | null | undefined, status: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  const when = days <= 0 ? 'today' : days === 1 ? 'yesterday' : days < 14 ? `${days} days ago` : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  if (status === 'unknown') return ` Last check ${when} got no answer; score is from the earlier check.`;
  if (status === 'catch-all') return ` Last check ${when}: catch-all domain, cannot be proven.`;
  if (status === 'role_based') return ` Checked ${when}: shared inbox like info@ or contact@, which we never send to. Use a named person's address.`;
  if (status === 'do_not_mail') return ` Checked ${when}: flagged as do not mail.`;
  return ` Checked ${when}.`;
};

type VerificationState = 'verified' | 'unknown' | 'bounced';

/**
 * Three states, on purpose. An address either has a check behind it, has failed
 * one, or has neither — which is also what an address the user typed in looks
 * like. Anything finer belongs in the tooltip, not the column.
 */
function verificationState(contact: Contact, override?: VerifiedOverride): VerificationState {
  const emailStatus = override ? override.emailStatus : contact.emailStatus;
  const confidence = override ? override.enrichmentConfidence : contact.enrichmentConfidence;
  if (emailStatus === 'bounced') return 'bounced';
  if (confidence != null && confidence >= VERIFIED_CONFIDENCE) return 'verified';
  return 'unknown';
}

const AVAILABLE_TOOLS: Tool[] = [
  { id: 'find-email', name: 'Find Email', description: 'Find professional email addresses using multiple providers', creditCost: 2, enabled: false },
  { id: 'verify-email', name: 'Verify Email', description: 'Check if an email address is valid and deliverable', creditCost: 1, enabled: false },
  { id: 'company-lookup', name: 'Company Lookup', description: 'Enrich company data from Companies House', creditCost: 1, enabled: false },
  { id: 'ai-enrichment', name: 'AI Enrichment', description: 'Use AI to research and populate custom fields', creditCost: 3, enabled: false },
];

interface ContactsTableProps {
  contacts: Contact[];
  isLoading: boolean;
  deleteContact: (id: number) => Promise<boolean>;
  bulkDelete: (ids: number[]) => Promise<boolean>;
  updateContact: (id: number, field: string, value: string) => Promise<boolean>;
  pagination?: { page: number; limit: number; total: number; totalPages: number };
  onPageChange?: (page: number) => void;
  emptyMessage?: string;
  searchQuery?: string;
  onClearSearch?: () => void;
  toolsButtonRef?: React.MutableRefObject<(() => void) | null>;
  /**
   * True when the table sits inside a padded card (the campaign Contacts tab)
   * rather than running to the bottom of the screen. The card's own padding
   * then supplies the gap below the footer row.
   */
  inCard?: boolean;
  /**
   * What a row is called in counts, row actions, dialogs and toasts. Lists
   * say contact; the campaign editor says lead.
   */
  noun?: { singular: string; plural: string };
}

export function ToolsButton({ onClick, hasEnabled }: { onClick: () => void; hasEnabled: boolean }) {
  return (
    <Button variant="primary" onClick={onClick} className="flex items-center gap-2 px-6">
      <WrenchScrewdriverIcon className="h-4 w-4" />
      Tools
      {hasEnabled && <span className="w-2 h-2 rounded-full bg-white" />}
      <svg className="h-4 w-4 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="15" y1="3" x2="15" y2="21" />
      </svg>
    </Button>
  );
}

const capitalise = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

export function ContactsTable({
  contacts,
  isLoading,
  deleteContact,
  bulkDelete,
  updateContact,
  pagination,
  onPageChange,
  emptyMessage = 'No contacts yet.',
  searchQuery,
  onClearSearch,
  toolsButtonRef,
  inCard = false,
  noun = { singular: 'contact', plural: 'contacts' },
}: ContactsTableProps) {
  const surface = inCard ? 'card' : 'page';
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingCell, setEditingCell] = useState<{ rowId: number; columnId: string } | null>(null);
  // Sets, not single ids: several rows can be finding or checking at once,
  // and starting a second must not clear the first's spinner.
  const [enrichingIds, setEnrichingIds] = useState<Set<number>>(new Set());
  const [verifyingIds, setVerifyingIds] = useState<Set<number>>(new Set());
  const track = (setter: Dispatch<SetStateAction<Set<number>>>, id: number, on: boolean) =>
    setter(prev => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next; });
  const [verifiedOverrides, setVerifiedOverrides] = useState<Map<number, VerifiedOverride>>(new Map());
  const [toolsOpen, setToolsOpen] = useState(false);
  const [tools, setTools] = useState<Tool[]>(AVAILABLE_TOOLS);

  const enabledTools = tools.filter(t => t.enabled);
  const totalCreditCost = enabledTools.reduce((sum, t) => sum + t.creditCost, 0);

  const toggleTool = (toolId: string) => {
    setTools(prev => prev.map(t => t.id === toolId ? { ...t, enabled: !t.enabled } : t));
  };

  // Expose toggle to parent via ref
  if (toolsButtonRef) {
    toolsButtonRef.current = () => setToolsOpen(prev => !prev);
  }

  const { selectedIds, toggle, selectAll, clearSelection, selectedCount } = useDataTableSelection({ data: contacts, getRowId: (c) => c.id });

  // Sorting. Text columns start A to Z and Verified starts with verified
  // contacts first; a second click reverses, a third returns to the list's
  // own order. The order is fixed when a sort is chosen or the set of rows
  // changes, not on every edit or Verify, so a row does not jump away from
  // the cursor.
  const [sort, setSort] = useState<{ columnId: string; direction: 'asc' | 'desc' } | null>(null);
  const cycleSort = (columnId: string) => setSort(prev => {
    const first: 'asc' | 'desc' = columnId === 'emailStatus' ? 'desc' : 'asc';
    const second: 'asc' | 'desc' = first === 'asc' ? 'desc' : 'asc';
    if (!prev || prev.columnId !== columnId) return { columnId, direction: first };
    if (prev.direction === first) return { columnId, direction: second };
    return null;
  });
  const idsKey = contacts.map(c => c.id).join(',');
  const sortOrder = useMemo(() => {
    if (!sort) return null;
    const dir = sort.direction === 'desc' ? -1 : 1;
    if (sort.columnId === 'emailStatus') {
      // Bounced < not verified < verified, then by confidence within each.
      // Rows with no email have nothing to sort on and stay at the bottom.
      const strength = (c: Contact) => {
        const override = verifiedOverrides.get(c.id);
        const state = verificationState(c, override);
        const confidence = (override ? override.enrichmentConfidence : c.enrichmentConfidence) ?? 0;
        return (state === 'verified' ? 2 : state === 'unknown' ? 1 : 0) * 1000 + confidence;
      };
      return [...contacts]
        .sort((a, b) => {
          if (!a.email !== !b.email) return a.email ? -1 : 1;
          return dir * (strength(a) - strength(b));
        })
        .map(c => c.id);
    }
    const read = TEXT_SORT_FIELDS[sort.columnId];
    if (!read) return null;
    // Case-insensitive, numbers in natural order, and blanks at the bottom
    // whichever way the column is sorted.
    const text = (c: Contact) => (read(c) ?? '').trim();
    return [...contacts]
      .sort((a, b) => {
        const av = text(a);
        const bv = text(b);
        if (!av !== !bv) return av ? -1 : 1;
        return dir * av.localeCompare(bv, undefined, { sensitivity: 'base', numeric: true });
      })
      .map(c => c.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sort only on a new sort or a new set of rows
  }, [sort, idsKey]);
  const displayContacts = useMemo(() => {
    if (!sortOrder) return contacts;
    const position = new Map(sortOrder.map((id, i) => [id, i]));
    const at = (id: number) => position.get(id) ?? Number.MAX_SAFE_INTEGER;
    return [...contacts].sort((a, b) => at(a.id) - at(b.id));
  }, [contacts, sortOrder]);

  const columns: ColumnDefinition<Contact>[] = [
    { id: 'select', label: '', width: '144px', required: true, resizable: false, skeletonWidth: 'w-8' },
    { id: 'firstName', label: 'First Name', width: '200px', minWidth: 120, required: true, skeletonWidth: 'w-28', sortable: true },
    { id: 'lastName', label: 'Last Name', width: '200px', minWidth: 120, required: true, skeletonWidth: 'w-28', sortable: true },
    { id: 'email', label: 'Email', width: '240px', minWidth: 150, skeletonWidth: 'w-40', sortable: true },
    { id: 'emailStatus', label: 'Verified', width: '150px', minWidth: 120, skeletonWidth: 'w-10', sortable: true },
    { id: 'role', label: 'Role', width: '240px', minWidth: 100, skeletonWidth: 'w-28', sortable: true },
    { id: 'company', label: 'Company', width: '200px', minWidth: 120, skeletonWidth: 'w-32', sortable: true },
    { id: 'companyWebsite', label: 'Company Website', width: '200px', minWidth: 150, skeletonWidth: 'w-36' },
    { id: 'location', label: 'Location', width: '200px', minWidth: 100, skeletonWidth: 'w-24', sortable: true },
  ];

  const { visibleColumns, saveVisibleColumns } = useDataTableColumns({ columns, storageKey: STORAGE_KEY });

  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.page || 1;

  const handleAccessEmail = async (contact: Contact) => {
    if (!contact.prospectExternalId) {
      toast.error('No linked prospect for enrichment');
      return;
    }
    track(setEnrichingIds, contact.id, true);
    try {
      const startRes = await fetch('/api/people/enrich-async', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospectId: contact.prospectExternalId }),
      });
      if (!startRes.ok) {
        const err = await startRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to start enrichment');
      }
      const { jobId } = await startRes.json();

      const result = await new Promise<any>((resolve) => {
        const poll = async () => {
          try {
            const res = await fetch(`/api/people/enrich-status/${jobId}`, { credentials: 'include' });
            const data = await res.json();
            if (data.done) { resolve(data.result || { success: false }); return; }
          } catch {}
          setTimeout(poll, 2000);
        };
        poll();
      });

      if (result.success && result.email) {
        await updateContact(contact.id, 'email', result.email);
        // The server has already stored the enrichment score and check date
        // on the contact; mirror them so the Verified column shows the tick
        // now rather than after a reload.
        if (typeof result.confidence === 'number') {
          setVerifiedOverrides(prev => new Map(prev).set(contact.id, {
            enrichmentConfidence: result.confidence,
            emailStatus: 'active',
            emailCheckedAt: new Date().toISOString(),
            emailCheckStatus: 'valid',
          }));
        }
        toast.success(`Email found: ${result.email}`);
      } else {
        toast.error(`Could not find email for this ${noun.singular}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enrichment failed');
    } finally {
      track(setEnrichingIds, contact.id, false);
    }
  };

  const handleVerifyEmail = async (contact: Contact) => {
    if (!contact.email) return;
    track(setVerifyingIds, contact.id, true);
    try {
      // In a campaign the row id is the campaign link; the contacts route
      // needs the contact itself.
      const { contact: updated, verification } = await contactsService.verifyContactEmail(contact.contactId ?? contact.id);

      // The server decides what a result is allowed to change; mirror its row
      // rather than re-deriving the rule here.
      setVerifiedOverrides(prev => new Map(prev).set(contact.id, {
        enrichmentConfidence: updated.enrichmentConfidence,
        emailStatus: updated.emailStatus,
        emailCheckedAt: updated.emailCheckedAt,
        emailCheckStatus: updated.emailCheckStatus,
      }));

      if (verification.isRoleBased) {
        toast.error(`${contact.email} is a shared inbox like info@ or contact@. We don't send to these because they reach a team, not a person, and get flagged as spam. Use a named person's address instead.`);
      } else if (verification.status === 'valid') {
        toast.success(`${contact.email} is deliverable (${verification.confidence}%)`);
      } else if (verification.status === 'invalid') {
        toast.error(`${contact.email} does not exist — marked as bounced`);
      } else if (verification.isCatchAll) {
        toast.warning(`${contact.email} sits on a catch-all domain, so it cannot be proven either way`);
      } else if (updated.enrichmentConfidence && updated.enrichmentConfidence >= 90) {
        toast.warning(`Could not re-check ${contact.email} today (${verification.status}). Keeping the earlier result.`);
      } else {
        toast.warning(verification.status === 'do_not_mail'
          ? `${contact.email} is flagged as do not mail, so it won't be sent to.`
          : `Could not confirm ${contact.email} (${verification.status})`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      track(setVerifyingIds, contact.id, false);
    }
  };

  const handleDeleteClick = (contact: Contact) => { setContactToDelete(contact); setDeleteDialogOpen(true); };

  const handleConfirmDelete = async () => {
    if (!contactToDelete) return;
    setIsDeleting(true);
    const success = await deleteContact(contactToDelete.id);
    setIsDeleting(false);
    if (success) { toast.success(`${capitalise(noun.singular)} deleted`); setDeleteDialogOpen(false); setContactToDelete(null); }
    else toast.error(`Failed to delete ${noun.singular}`);
  };

  const handleBulkDeleteClick = () => { if (selectedCount === 0) return; setBulkDeleteDialogOpen(true); };

  const handleConfirmBulkDelete = async () => {
    setIsDeleting(true);
    const success = await bulkDelete(Array.from(selectedIds) as number[]);
    setIsDeleting(false);
    if (success) { toast.success(`${selectedCount} ${selectedCount === 1 ? noun.singular : noun.plural} deleted`); setBulkDeleteDialogOpen(false); clearSelection(); }
    else toast.error(`Failed to delete ${noun.plural}`);
  };

  const rowActions: RowAction<Contact>[] = [
    { label: `Delete ${noun.singular}`, onClick: handleDeleteClick, variant: 'destructive' },
  ];

  const handleCellEdit = async (contactId: number, field: string, value: string) => {
    const success = await updateContact(contactId, field, value);
    if (!success) toast.error('Failed to save changes');
  };

  const allEditableFields: { field: keyof Contact; columnId: string }[] = [
    { field: 'firstName', columnId: 'firstName' },
    { field: 'lastName', columnId: 'lastName' },
    { field: 'email', columnId: 'email' },
    { field: 'title', columnId: 'role' },
    { field: 'company', columnId: 'company' },
    { field: 'companyWebsite', columnId: 'companyWebsite' },
    { field: 'location', columnId: 'location' },
  ];

  const editableFields = allEditableFields.filter(({ columnId }) => visibleColumns.includes(columnId)).map(({ field }) => field);

  const getNextEditableCell = (id: number, field: keyof Contact) => {
    const i = editableFields.indexOf(field);
    if (i === -1) return null;
    if (i < editableFields.length - 1) return { rowId: id, columnId: editableFields[i + 1] };
    const ri = contacts.findIndex(c => c.id === id);
    if (ri < contacts.length - 1) return { rowId: contacts[ri + 1].id, columnId: editableFields[0] };
    return null;
  };

  const getPreviousEditableCell = (id: number, field: keyof Contact) => {
    const i = editableFields.indexOf(field);
    if (i === -1) return null;
    if (i > 0) return { rowId: id, columnId: editableFields[i - 1] };
    const ri = contacts.findIndex(c => c.id === id);
    if (ri > 0) return { rowId: contacts[ri - 1].id, columnId: editableFields[editableFields.length - 1] };
    return null;
  };

  const renderEditableCell = (contact: Contact, field: keyof Contact) => {
    const isEditing = editingCell?.rowId === contact.id && editingCell?.columnId === field;
    const value = contact[field] as string | null;
    return (
      <DataTableEditableCell
        value={value}
        rowId={contact.id}
        columnId={field}
        isEditing={isEditing}
        onStartEdit={() => setEditingCell({ rowId: contact.id, columnId: field })}
        onSave={(v) => { setEditingCell(null); handleCellEdit(contact.id, field, v); }}
        onCancel={() => setEditingCell(null)}
        onTab={(v) => { handleCellEdit(contact.id, field, v); setEditingCell(getNextEditableCell(contact.id, field)); }}
        onShiftTab={(v) => { handleCellEdit(contact.id, field, v); setEditingCell(getPreviousEditableCell(contact.id, field)); }}
      />
    );
  };

  const columnsWithCells: ColumnDefinition<Contact>[] = columns.map(col => {
    if (col.id === 'select') {
      return {
        ...col,
        cell: (contact: Contact) => (
          <div className="flex items-center gap-3">
            <Checkbox checked={selectedIds.has(contact.id)} onCheckedChange={() => toggle(contact.id)} />
            {contact.profileImageUrl
              && !contact.profileImageUrl.startsWith('data:image/gif')
              && !contact.profileImageUrl.includes('static.licdn.com') ? (
              <Avatar className="flex-shrink-0 outline-none">
                <AvatarImage
                  src={contact.profileImageUrl.includes('media.licdn.com')
                    ? `/api/images/proxy?url=${encodeURIComponent(contact.profileImageUrl)}`
                    : contact.profileImageUrl}
                  alt={`${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim()}
                />
                <AvatarFallback className="bg-surface-avatar"><UserIcon className="h-4 w-4 text-muted-foreground" /></AvatarFallback>
              </Avatar>
            ) : (
              <div className="w-8 h-8 rounded-full bg-surface-avatar flex items-center justify-center flex-shrink-0">
                <UserIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            {contact.linkedinUrl && (
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 text-primary hover:text-primary/80 transition-colors" onClick={(e) => e.stopPropagation()}>
                      <LinkedInIcon className="h-4 w-4" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">{contact.linkedinUrl}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )
      };
    }
    if (col.id === 'firstName') return { ...col, cell: (contact: Contact) => renderEditableCell(contact, 'firstName') };
    if (col.id === 'lastName') return { ...col, cell: (contact: Contact) => renderEditableCell(contact, 'lastName') };
    if (col.id === 'email') return {
      ...col,
      cell: (contact: Contact) => {
        const isEditing = editingCell?.rowId === contact.id && editingCell?.columnId === 'email';
        if (contact.email || isEditing) return renderEditableCell(contact, 'email');
        return (
          <div className="absolute inset-0 flex items-center pl-4 pr-2 gap-2">
            <Button
              variant="secondary"
              size="xs"
              className="text-xs whitespace-nowrap shrink-0 shadow-lg z-10"
              disabled={enrichingIds.has(contact.id) || !contact.prospectExternalId}
              onClick={(e) => { e.stopPropagation(); handleAccessEmail(contact); }}
            >
              {enrichingIds.has(contact.id) ? 'Finding...' : 'Find Email'}
            </Button>
            <div
              className="flex-1 h-9 rounded-[10px] cursor-pointer hover:bg-accent transition-colors"
              onClick={() => setEditingCell({ rowId: contact.id, columnId: 'email' })}
            />
          </div>
        );
      }
    };
    if (col.id === 'emailStatus') return {
      ...col,
      cell: (contact: Contact) => {
        if (!contact.email) return <span className="text-muted-foreground">&mdash;</span>;
        const override = verifiedOverrides.get(contact.id);
        const state = verificationState(contact, override);
        const confidence = override ? override.enrichmentConfidence : contact.enrichmentConfidence;
        const checked = override && override.emailCheckedAt !== undefined
          ? checkedLabel(override.emailCheckedAt, override.emailCheckStatus)
          : checkedLabel(contact.emailCheckedAt, contact.emailCheckStatus);
        // A pastel puck with a dark glyph, using the documented status-pill
        // pairings: the tick chip's green, the error badge's red, and the
        // avatar puck's neutral for "we don't know". Heroicons has no bare
        // question mark (only the circled one), so that glyph is text.
        const Glyph = state === 'verified' ? CheckGlyph : state === 'bounced' ? XGlyph : null;
        const puck = state === 'verified'
          ? 'bg-success-surface-strong text-success-foreground-deep'
          : state === 'bounced'
            ? 'bg-danger-surface-strong text-danger-foreground-strong'
            : 'bg-surface-avatar text-secondary-foreground';
        const hint = state === 'verified'
          ? `Verified (${confidence}%).${checked}`
          : state === 'bounced'
            ? `Bounced. Will not be sent to.${checked}`
            : `Not verified. Will not be sent to until it is.${checked}`;
        return (
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${puck}`} aria-label={hint}>
                    {Glyph ? <Glyph className="h-3.5 w-3.5" /> : <span className="text-xs font-semibold leading-none">?</span>}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top"><p className="text-xs">{hint}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Always offered when there is an address. Addresses go stale
                between enrichment and send, so a verified row needs a
                re-check as much as an unknown one needs a first check. */}
            <Button
              variant="secondary"
              size="xs"
              className="text-xs whitespace-nowrap shrink-0"
              disabled={verifyingIds.has(contact.id)}
              onClick={(e) => { e.stopPropagation(); handleVerifyEmail(contact); }}
            >
              {verifyingIds.has(contact.id) ? 'Checking...' : 'Verify'}
            </Button>
          </div>
        );
      }
    };
    if (col.id === 'role') return { ...col, cell: (contact: Contact) => renderEditableCell(contact, 'title') };
    if (col.id === 'company') return { ...col, cell: (contact: Contact) => renderEditableCell(contact, 'company') };
    if (col.id === 'companyWebsite') return { ...col, cell: (contact: Contact) => renderEditableCell(contact, 'companyWebsite') };
    if (col.id === 'location') return { ...col, cell: (contact: Contact) => renderEditableCell(contact, 'location') };
    return col;
  });

  return (
    <>
      {/* A flat grid between two rules, no card around it. The caller runs
          this block flush to its container's edges; the Tools panel opens as
          a column beside the table behind a single rule. */}
      <div className="flex flex-1 min-h-0 border-y border-table-grid">
        <div className="bg-table flex-1 min-h-0 overflow-hidden relative flex flex-col" ref={tableContainerRef} style={{ contain: 'layout paint' }}>
          <DataTable
            data={displayContacts}
            columns={columnsWithCells}
            sort={sort}
            onSortChange={cycleSort}
            isLoading={isLoading}
            resizable
            resizeConfig={{ storageKey: 'contacts_columnWidths' }}
            disableRowHover
            selection={{ selectedIds, onToggle: toggle, onSelectAll: selectAll, onClearSelection: clearSelection }}
            rowActions={rowActions}
            columnVisibility={{ visibleColumns, onSave: saveVisibleColumns, storageKey: STORAGE_KEY }}
            emptyState={searchQuery ? (
              <>
                <p className="text-muted-foreground mb-4">No {noun.plural} found matching "{searchQuery}"</p>
                {onClearSearch && <Button variant="primary" onClick={onClearSearch}>Clear search</Button>}
              </>
            ) : <p className="text-foreground">{emptyMessage}</p>}
          />
        </div>

        {/* Tools Sidebar */}
        {toolsOpen && (
          <div className="w-[30%] max-w-[28rem] min-w-[20rem] shrink-0 bg-table border-l border-table-grid overflow-y-auto flex flex-col">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h3 className="text-lg font-medium">Tools</h3>
              <button onClick={() => setToolsOpen(false)} className="h-8 w-8 flex items-center justify-center rounded-full bg-surface-raised border border-control-border shadow-lg hover:bg-accent transition-colors">
                <XMarkIcon className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
            <div className="px-5 pb-5 space-y-4">
              {tools.map(tool => (
                <div key={tool.id} className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{tool.name}</p>
                    <TooltipProvider>
                      <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{tool.description}</p>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs">
                          <p className="text-xs">{tool.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="scale-75 origin-right"><CreditCostPill amount={tool.creditCost} suffix="contact" /></span>
                    <Switch
                      checked={tool.enabled}
                      onCheckedChange={() => toggleTool(tool.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
            {enabledTools.length > 0 && (
              <div className="mt-auto px-5 py-4 border-t border-border">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{enabledTools.length} tool{enabledTools.length !== 1 ? 's' : ''} enabled</span>
                  <span className="scale-75 origin-left"><CreditCostPill amount={totalCreditCost} suffix="contact" /></span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {pagination && onPageChange ? (
        <DataTablePagination
          config={{ variant: 'simple', currentPage, totalPages, onPageChange }}
          totalLabel={noun.plural}
          total={pagination.total}
          surface={surface}
        />
      ) : (
        <TableFooter surface={surface}>
          <span className="text-sm text-foreground whitespace-nowrap">
            <span className="font-bold">{contacts.length}</span> {contacts.length === 1 ? noun.singular : noun.plural}
          </span>
        </TableFooter>
      )}

      <SelectionActionBar selectedCount={selectedCount} isVisible={selectedCount > 0} onClearSelection={clearSelection} itemName={noun} tableContainerRef={tableContainerRef} selectedRowIds={Array.from(selectedIds).map(String)}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="primary" size="xs" className="mr-2">
              Enrichment
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72">
            <DropdownMenuItem onClick={async () => {
              const selected = contacts.filter(c => selectedIds.has(c.id) && !c.email);
              if (selected.length === 0) { toast.error(`All selected ${noun.plural} already have emails`); return; }
              for (const contact of selected) { await handleAccessEmail(contact); }
            }} className="flex items-center justify-between">
              <span>Find Email ({selectedCount} row{selectedCount !== 1 ? 's' : ''})</span>
              <span className="scale-75 origin-right"><CreditCostPill amount={2 * selectedCount} /></span>
            </DropdownMenuItem>
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div>
                    <DropdownMenuItem
                      disabled={enabledTools.length === 0}
                      onClick={enabledTools.length > 0 ? async () => {
                        toast.info(`Running ${enabledTools.length} tool${enabledTools.length !== 1 ? 's' : ''} on ${selectedCount} ${selectedCount !== 1 ? noun.plural : noun.singular}...`);
                      } : undefined}
                      className="flex items-center justify-between"
                    >
                      <span>Run All Tools ({selectedCount} row{selectedCount !== 1 ? 's' : ''})</span>
                      {enabledTools.length > 0 && (
                        <span className="scale-75 origin-right"><CreditCostPill amount={totalCreditCost * selectedCount} /></span>
                      )}
                    </DropdownMenuItem>
                  </div>
                </TooltipTrigger>
                {enabledTools.length === 0 && (
                  <TooltipContent side="right">
                    <p className="text-xs">Enable tools in the Tools sidebar first</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuItem onClick={() => setToolsOpen(true)}>
              Configure Tools
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="destructive" size="xs" onClick={handleBulkDeleteClick}>Delete</Button>
      </SelectionActionBar>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {capitalise(noun.singular)}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{toTitleCase(contactToDelete?.firstName)} {toTitleCase(contactToDelete?.lastName)}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>{isDeleting ? 'Deleting...' : 'Delete'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {selectedCount} {capitalise(selectedCount === 1 ? noun.singular : noun.plural)}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedCount} {selectedCount === 1 ? noun.singular : noun.plural}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmBulkDelete} disabled={isDeleting}>{isDeleting ? 'Deleting...' : `Delete ${selectedCount} ${capitalise(selectedCount === 1 ? noun.singular : noun.plural)}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
